/**
 * CollectionsCalendar — a daily-diary view of every Promise to Pay across
 * all ageing cases. Ported from the Monday vibe app, rebuilt against our
 * structured P2P endpoints (`GET /api/collections/ageing/p2ps`) instead of
 * the vibe app's regex-parsing of Monday update text.
 *
 * Layout:
 *
 *   ┌──────────────────────────────────────────┬────────────────────┐
 *   │     ◀  June 2026  ▶     [Today]          │    Selected day    │
 *   ├──────┬──────┬──────┬──────┬──────┬──────┤    (DaySchedule)   │
 *   │ Mon  │ Tue  │ Wed  │ Thu  │ Fri  │ Sat… │                    │
 *   │  pill│  pill│      │ pill │      │      │  P2P #1   £2,500   │
 *   │  pill│      │      │      │      │      │  P2P #2   £950     │
 *   │  ··· │      │      │      │      │      │  ···               │
 *   └──────┴──────┴──────┴──────┴──────┴──────┴────────────────────┘
 *
 * Filters (top right): All P2Ps · Pending · Kept · Broken · Cancelled  ·
 *                      All handlers · {handler dropdown}
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  ChevronLeft, ChevronRight, Loader2, Calendar as CalIcon,
  CheckCircle2, AlertOctagon, Ban, ExternalLink, XCircle, RefreshCw, ChevronDown,
  Pencil, Trash2, Search, X,
} from "lucide-react";
import { toast } from "sonner";
import { listAgeingP2Ps, getAgeingHandlers, updateAgeingP2P, deleteAgeingP2P, rescheduleAgeingP2P, listLegalEvents } from "@/lib/api";
import { useRegion } from "@/contexts/RegionContext";
import { cur, fmtMoney, isAusDesk, ageingBasePath, setCollectionsRegion } from "@/lib/regionFmt";

const NOTICE_TYPES = { pdn_expires: "PDN expires", dn_expires: "DN expires" };

const MONTHS = ["January", "February", "March", "April", "May", "June", "July",
                "August", "September", "October", "November", "December"];
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const STATUS_META = {
  pending:   { tone: "bg-amber-100 text-amber-900 border-amber-300",   dot: "bg-amber-500",   icon: null },
  kept:      { tone: "bg-emerald-100 text-emerald-900 border-emerald-300", dot: "bg-emerald-500", icon: CheckCircle2 },
  broken:    { tone: "bg-red-100 text-red-900 border-red-300",         dot: "bg-red-500",     icon: AlertOctagon },
  cancelled: { tone: "bg-slate-100 text-slate-700 border-slate-300",   dot: "bg-slate-400",   icon: Ban },
};

// Region-aware money + dates: £/en-GB on the UK desk, $/en-AU (Sydney "today") on the AUS desk.
const fmtGBP = (v) => fmtMoney(v, 0);
const locale = () => (isAusDesk() ? "en-AU" : "en-GB");
const regionToday = () => (isAusDesk()
  ? new Date(new Date().toLocaleString("en-US", { timeZone: "Australia/Sydney" }))
  : new Date());


export default function CollectionsCalendar() {
  const navigate = useNavigate();
  const { currentRegion } = useRegion();
  setCollectionsRegion(currentRegion);   // module flag → api interceptor tags region, money formatters switch
  const today = regionToday();
  const [month, setMonth] = useState(today.getMonth());
  const [year,  setYear]  = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [items,   setItems]   = useState([]);
  const [byDate,  setByDate]  = useState({});
  const [legalByDate, setLegalByDate] = useState({});
  const [handlers, setHandlers] = useState([]);
  const [filter, setFilter] = useState({ status: "all", handler: "all" });
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [searchMatches, setSearchMatches] = useState(null);

  // Debounce the search box → API param
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Wide-range lookup so a search finds promises in ANY month, not just the visible one
  useEffect(() => {
    if (!search) { setSearchMatches(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const now = new Date();
        const r = await listAgeingP2Ps({
          start: isoDate(new Date(now.getFullYear() - 2, now.getMonth(), 1)),
          end:   isoDate(new Date(now.getFullYear() + 1, now.getMonth() + 1, 0)),
          search,
        });
        if (!cancelled) setSearchMatches(r.items || []);
      } catch { if (!cancelled) setSearchMatches([]); }
    })();
    return () => { cancelled = true; };
  }, [search]);

  const jumpToMatch = (it) => {
    const d = new Date(it.p2p.promised_at + "T00:00:00");
    setMonth(d.getMonth());
    setYear(d.getFullYear());
    setTimeout(() => setSelectedDate(it.p2p.promised_at), 0);
  };

  // Load handlers once
  useEffect(() => {
    (async () => {
      try { const r = await getAgeingHandlers("collections"); setHandlers(r.handlers || r || []); }
      catch (e) { console.warn("handlers", e); }
    })();
  }, []);

  // Refetch P2Ps when month or filter changes (1 month window with a 7-day buffer either side)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const first = new Date(year, month, 1);
        const last  = new Date(year, month + 1, 0);
        const start = new Date(first); start.setDate(first.getDate() - 7);
        const end   = new Date(last);  end.setDate(last.getDate() + 7);
        const params = {
          start: isoDate(start),
          end:   isoDate(end),
          status:  filter.status  === "all" ? undefined : filter.status,
          handler: filter.handler === "all" ? undefined : filter.handler,
          search:  search || undefined,
        };
        const r = await listAgeingP2Ps(params);
        if (cancelled) return;
        setItems(r.items || []);
        setByDate(r.by_date || {});
        try {
          const le = await listLegalEvents({ from_date: params.start, to_date: params.end, limit: 1000 });
          if (!cancelled) setLegalByDate(groupNotices(le.events || []));
        } catch { /* notices are best-effort */ }
      } catch (e) {
        console.error("p2ps", e);
        toast.error(`Calendar load failed: ${e?.response?.data?.detail || e.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [month, year, filter.status, filter.handler, search]);

  // Re-select the same date when month changes — keeps the right pane stable
  useEffect(() => { setSelectedDate(null); }, [month, year]);

  // Build calendar grid — Monday-first
  const grid = useMemo(() => {
    const first = new Date(year, month, 1);
    const firstDow = (first.getDay() + 6) % 7; // 0 = Mon
    const daysIn = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= daysIn; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [month, year]);

  const totals = useMemo(() => {
    const t = { pending: 0, kept: 0, broken: 0, cancelled: 0, amount: 0 };
    for (const it of items) {
      const s = it.p2p?.status || "pending";
      t[s] = (t[s] || 0) + 1;
      if (s === "pending" || s === "kept") t.amount += Number(it.p2p?.promised_amount || 0);
    }
    return t;
  }, [items]);

  const goPrevMonth = () => {
    if (month === 0) { setMonth(11); setYear(year - 1); }
    else             { setMonth(month - 1); }
  };
  const goNextMonth = () => {
    if (month === 11) { setMonth(0); setYear(year + 1); }
    else              { setMonth(month + 1); }
  };
  const goToday = () => {
    setMonth(today.getMonth());
    setYear(today.getFullYear());
    setSelectedDate(isoDate(today));
  };

  const selectedItems = selectedDate ? (byDate[selectedDate] || []) : [];
  const selectedNotices = selectedDate ? (legalByDate[selectedDate] || []) : [];

  const reloadCalendar = async () => {
    try {
      const params = {
        start: isoDate(new Date(year, month, 1 - 7)),
        end:   isoDate(new Date(year, month + 1, 7)),
        status:  filter.status  === "all" ? undefined : filter.status,
        handler: filter.handler === "all" ? undefined : filter.handler,
        search:  search || undefined,
      };
      const r = await listAgeingP2Ps(params);
      setItems(r.items || []);
      setByDate(r.by_date || {});
      try {
        const le = await listLegalEvents({ from_date: params.start, to_date: params.end, limit: 1000 });
        setLegalByDate(groupNotices(le.events || []));
      } catch { /* notices are best-effort */ }
    } catch (e) {
      toast.error(`Reload failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  return (
    <div className="space-y-4 p-4" data-testid="collections-calendar">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b pb-3">
        <div className="flex items-center gap-2">
          <CalIcon className="w-5 h-5 text-blue-600" />
          <h1 className="text-xl font-semibold">Collections Calendar</h1>
          {isAusDesk() && <Badge className="bg-emerald-600 hover:bg-emerald-600 text-[10px]" data-testid="cal-region-badge">AUS</Badge>}
          <Badge variant="outline" className="ml-2">Promises to Pay</Badge>
          <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-300">PDN / DN expiries</Badge>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Account search */}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={isAusDesk() ? "Search account / SFA number…" : "Search account / SF number…"}
              className="h-8 w-[220px] pl-7 pr-7 text-xs"
              data-testid="cal-search-input"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput("")}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                data-testid="cal-search-clear"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {/* Status filter */}
          <Select value={filter.status} onValueChange={(v) => setFilter((f) => ({ ...f, status: v }))}>
            <SelectTrigger className="h-8 w-[140px] text-xs" data-testid="cal-filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="kept">Kept</SelectItem>
              <SelectItem value="broken">Broken</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          {/* Handler filter */}
          <Select value={filter.handler} onValueChange={(v) => setFilter((f) => ({ ...f, handler: v }))}>
            <SelectTrigger className="h-8 w-[160px] text-xs" data-testid="cal-filter-handler">
              <SelectValue placeholder="Handler" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All handlers</SelectItem>
              {handlers.map((h) => (
                <SelectItem key={h} value={h}>{h}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Search matches across all months */}
      {search && searchMatches !== null && (
        <div className="rounded-md border bg-blue-50/50 border-blue-200 px-3 py-2" data-testid="cal-search-results">
          {searchMatches.length === 0 ? (
            <p className="text-xs text-slate-600">No promises found for “{search}” in the last 2 years / next 12 months.</p>
          ) : (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] font-semibold text-blue-900 mr-1">
                {searchMatches.length} promise{searchMatches.length === 1 ? "" : "s"} found:
              </span>
              {searchMatches.slice(0, 12).map((it) => {
                const meta = STATUS_META[it.p2p?.status || "pending"] || STATUS_META.pending;
                return (
                  <button
                    key={it.p2p.id}
                    type="button"
                    onClick={() => jumpToMatch(it)}
                    className={`text-[11px] px-2 py-0.5 rounded border hover:ring-1 hover:ring-blue-400 transition ${meta.tone}`}
                    title={`${it.customer_name} · ${fmtGBP(it.p2p.promised_amount)} · ${it.p2p.status}`}
                    data-testid={`cal-search-match-${it.p2p.id}`}
                  >
                    <span className="font-semibold">{new Date(it.p2p.promised_at + "T00:00:00").toLocaleDateString(locale(), { day: "numeric", month: "short", year: "2-digit" })}</span>
                    <span className="mx-1 opacity-60">·</span>
                    {shortName(it.customer_name)}
                    <span className="mx-1 opacity-60">·</span>
                    {fmtGBP(it.p2p.promised_amount)}
                  </button>
                );
              })}
              {searchMatches.length > 12 && (
                <span className="text-[11px] text-blue-800">+{searchMatches.length - 12} more</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Month nav + totals */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={goPrevMonth} data-testid="cal-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
          <h2 className="text-lg font-medium min-w-[170px] text-center" data-testid="cal-month-label">{MONTHS[month]} {year}</h2>
          <Button variant="outline" size="sm" onClick={goNextMonth} data-testid="cal-next-month"><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={goToday} className="text-xs" data-testid="cal-today">Today</Button>
        </div>

        <div className="flex items-center gap-3 text-xs">
          <span><span className="font-semibold">{totals.pending}</span> pending</span>
          <span><span className="font-semibold text-emerald-700">{totals.kept}</span> kept</span>
          <span><span className="font-semibold text-red-700">{totals.broken}</span> broken</span>
          <span className="text-muted-foreground">·</span>
          <span><span className="font-semibold">{fmtGBP(totals.amount)}</span> outstanding</span>
        </div>
      </div>

      {/* Body: calendar grid + day panel */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr,360px] gap-4">
        {/* Grid */}
        <div className="border rounded-lg overflow-hidden bg-white">
          {/* DOW header */}
          <div className="grid grid-cols-7 bg-slate-50 border-b text-[10px] font-semibold uppercase tracking-wider text-slate-600">
            {DOW.map((d) => <div key={d} className="px-2 py-1.5 text-center">{d}</div>)}
          </div>
          {/* Days */}
          <div className="grid grid-cols-7" data-testid="cal-grid">
            {grid.map((d, i) => {
              if (!d) return <div key={`empty-${i}`} className="bg-slate-50/40 min-h-[100px] border-r border-b" />;
              const iso = isoDate(d);
              const list = byDate[iso] || [];
              const notices = legalByDate[iso] || [];
              const isToday = iso === isoDate(today);
              const isSelected = iso === selectedDate;
              const isPast = d < new Date(today.getFullYear(), today.getMonth(), today.getDate());
              return (
                <button
                  type="button"
                  key={iso}
                  onClick={() => setSelectedDate(iso)}
                  className={`relative min-h-[100px] border-r border-b p-1.5 text-left hover:bg-blue-50/40 transition ${
                    isSelected ? "bg-blue-100/60 ring-2 ring-inset ring-blue-400" : ""
                  } ${isToday ? "bg-amber-50/40" : ""}`}
                  data-testid={`cal-day-${iso}`}
                >
                  <div className={`text-xs font-semibold ${isPast ? "text-slate-400" : "text-slate-700"}`}>
                    {d.getDate()}
                    {isToday && <span className="ml-1 text-[9px] uppercase text-amber-700">Today</span>}
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {notices.slice(0, 2).map((ev) => (
                      <div
                        key={ev.event_id}
                        className="text-[10px] px-1.5 py-0.5 rounded border truncate bg-rose-50 text-rose-700 border-rose-300"
                        title={`${NOTICE_TYPES[ev.type]} · ${ev.customer_name || ""}`}
                        data-testid={`cal-notice-${ev.event_id}`}
                      >
                        <span className="font-semibold">{ev.type === "pdn_expires" ? "PDN" : "DN"}</span>
                        <span className="ml-1 opacity-80">{shortName(ev.customer_name || "expires")}</span>
                      </div>
                    ))}
                    {notices.length > 2 && (
                      <div className="text-[10px] text-rose-500 font-medium">+{notices.length - 2} notices</div>
                    )}
                    {list.slice(0, 4).map((it) => {
                      const status = it.p2p?.status || "pending";
                      const meta = STATUS_META[status] || STATUS_META.pending;
                      return (
                        <div
                          key={it.p2p.id}
                          className={`text-[10px] px-1.5 py-0.5 rounded border truncate ${meta.tone}`}
                          title={`${it.customer_name} · ${cur()}${it.p2p.promised_amount}`}
                        >
                          <span className="font-semibold">{fmtGBP(it.p2p.promised_amount)}</span>
                          <span className="ml-1 opacity-80">{shortName(it.customer_name)}</span>
                        </div>
                      );
                    })}
                    {list.length > 4 && (
                      <div className="text-[10px] text-slate-500 font-medium">+{list.length - 4} more</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          {loading && (
            <div className="text-center text-xs text-muted-foreground py-2">
              <Loader2 className="w-3 h-3 animate-spin inline-block mr-1" /> Loading P2Ps…
            </div>
          )}
        </div>

        {/* Day panel */}
        <DayPanel
          isoDate={selectedDate}
          items={selectedItems}
          notices={selectedNotices}
          onOpenCase={(caseId) => navigate(`${ageingBasePath()}/${caseId}?tab=p2ps`)}
          onReload={reloadCalendar}
        />
      </div>
    </div>
  );
}


function DayPanel({ isoDate: dateStr, items, notices = [], onOpenCase, onReload }) {
  if (!dateStr) {
    return (
      <div className="border rounded-lg bg-white p-6 text-center text-sm text-muted-foreground">
        Pick a day on the calendar to see scheduled P2Ps.
      </div>
    );
  }
  const total = items.reduce((s, i) => s + Number(i.p2p?.promised_amount || 0), 0);
  return (
    <div className="border rounded-lg bg-white flex flex-col max-h-[70vh]" data-testid="cal-day-panel">
      <div className="border-b px-4 py-3">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{prettyDate(dateStr)}</div>
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold">{items.length} promise{items.length === 1 ? "" : "s"} to pay</h3>
          {items.length > 0 && <Badge variant="secondary">{fmtGBP(total)}</Badge>}
        </div>
      </div>
      <div className="overflow-auto flex-1">
        {notices.length > 0 && (
          <div className="border-b bg-rose-50/40 px-4 py-2" data-testid="cal-day-notices">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-rose-700 mb-1">Notice expiries</div>
            <ul className="space-y-1">
              {notices.map((ev) => (
                <li key={ev.event_id} className="flex items-center gap-2 text-xs">
                  <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-300 text-[9px] shrink-0">
                    {NOTICE_TYPES[ev.type] || ev.label}
                  </Badge>
                  <button type="button" className="truncate font-medium text-blue-700 hover:underline text-left"
                    onClick={() => ev.case_id && onOpenCase(ev.case_id)}>
                    {ev.customer_name || ev.case_id}
                  </button>
                  {ev.loan_application_id && (
                    <span className="text-[10px] text-muted-foreground shrink-0">{ev.loan_application_id}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        {items.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">No P2Ps for this day.</div>
        ) : (
          <ul className="divide-y">
            {items.map((it) => <P2PRow key={it.p2p.id} item={it} onOpenCase={onOpenCase} onReload={onReload} />)}
          </ul>
        )}
      </div>
    </div>
  );
}


function groupNotices(events) {
  const out = {};
  for (const ev of events) {
    if (!NOTICE_TYPES[ev.type] || !ev.date) continue;
    (out[ev.date] = out[ev.date] || []).push(ev);
  }
  return out;
}


function P2PRow({ item, onOpenCase, onReload }) {
  const p = item.p2p;
  const status = p.status || "pending";
  const meta = STATUS_META[status];
  const Icon = meta.icon;
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);

  const setStatus = async (newStatus) => {
    try {
      setBusy(true);
      const patch = { status: newStatus };
      if (newStatus === "kept") patch.kept_amount = p.promised_amount;
      await updateAgeingP2P(item.case_id, p.id, patch);
      toast.success(`P2P marked ${newStatus}`);
      onReload && (await onReload());
    } catch (e) {
      toast.error("Failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete this P2P (${cur()}${p.promised_amount?.toLocaleString()} on ${p.promised_at})? This is permanent.`)) return;
    try {
      setBusy(true);
      await deleteAgeingP2P(item.case_id, p.id);
      toast.success("P2P deleted");
      onReload && (await onReload());
    } catch (e) {
      toast.error("Failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="p-3 hover:bg-slate-50 transition" data-testid={`cal-p2p-${p.id}`}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm truncate">
            {item.customer_name}
            {p.rescheduled_from && (
              <Badge className="ml-1.5 bg-blue-100 text-blue-800 border-blue-300 text-[9px] px-1.5 h-4" title="Rescheduled from a missed P2P">↻ rescheduled</Badge>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground">
            {item.loan_application_id || item.loandisk_loan_id || "—"}
            {item.handler && <> · <span className="text-slate-700 font-medium">{item.handler}</span></>}
            {item.days_past_due != null && <> · <span className="text-red-700">{item.days_past_due} dpd</span></>}
          </div>
        </div>
        <Badge className={`${meta.tone} text-[10px] gap-1 shrink-0`}>
          {Icon && <Icon className="w-3 h-3" />}
          {status}
        </Badge>
      </div>
      <div className="flex items-center justify-between mt-1.5">
        <div className="text-sm">
          <span className="font-semibold">{fmtGBP(p.promised_amount)}</span>
          <span className="text-[11px] text-muted-foreground ml-1">via {p.method?.replace("_", " ")}</span>
        </div>
        <div className="flex items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" disabled={busy} className="h-6 text-[10px] px-1.5 bg-white" data-testid={`cal-p2p-${p.id}-status-menu`}>
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <>Actions <ChevronDown className="w-2.5 h-2.5 ml-0.5" /></>}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-[10px]">Mark this P2P as…</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => setStatus("kept")} className="text-[11px]" data-testid={`cal-p2p-${p.id}-mark-kept`}>
                <CheckCircle2 className="w-3.5 h-3.5 mr-2 text-emerald-600" /> Kept — paid as promised
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatus("broken")} className="text-[11px]" data-testid={`cal-p2p-${p.id}-mark-broken`}>
                <XCircle className="w-3.5 h-3.5 mr-2 text-rose-600" /> Missed — no reschedule
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setRescheduleOpen(true)} className="text-[11px]" data-testid={`cal-p2p-${p.id}-reschedule`}>
                <RefreshCw className="w-3.5 h-3.5 mr-2 text-amber-600" /> Missed → New P2P
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatus("cancelled")} className="text-[11px]" data-testid={`cal-p2p-${p.id}-mark-cancelled`}>
                <Ban className="w-3.5 h-3.5 mr-2 text-slate-500" /> Cancel — customer withdrew
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {status === "pending" && (
                <DropdownMenuItem onClick={() => setEditOpen(true)} className="text-[11px]" data-testid={`cal-p2p-${p.id}-edit`}>
                  <Pencil className="w-3.5 h-3.5 mr-2 text-slate-700" /> Edit details
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={remove} className="text-[11px] text-rose-700" data-testid={`cal-p2p-${p.id}-delete`}>
                <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            size="sm" variant="ghost" className="h-6 text-[10px] px-1.5"
            onClick={() => onOpenCase(item.case_id)}
            data-testid={`cal-p2p-${p.id}-open`}
          >
            <ExternalLink className="w-3 h-3" />
          </Button>
        </div>
      </div>
      {p.notes && (
        <div className="text-[11px] text-muted-foreground mt-1 italic border-l-2 border-slate-200 pl-2">
          {p.notes}
        </div>
      )}

      {editOpen && (
        <CalP2PEditDialog
          p={p}
          caseId={item.case_id}
          onClose={() => setEditOpen(false)}
          onSaved={async () => { setEditOpen(false); onReload && (await onReload()); }}
        />
      )}
      {rescheduleOpen && (
        <CalP2PRescheduleDialog
          p={p}
          caseId={item.case_id}
          onClose={() => setRescheduleOpen(false)}
          onSaved={async () => { setRescheduleOpen(false); onReload && (await onReload()); }}
        />
      )}
    </li>
  );
}


// ── Edit P2P (pending only) — calendar local copy ─────────────────────────
function CalP2PEditDialog({ p, caseId, onClose, onSaved }) {
  const [date,   setDate]   = useState(p.promised_at || "");
  const [amount, setAmount] = useState(String(p.promised_amount ?? ""));
  const [method, setMethod] = useState(p.method || "bank_transfer");
  const [notes,  setNotes]  = useState(p.notes || "");
  const [busy,   setBusy]   = useState(false);

  const save = async () => {
    if (!date || !amount) { toast.error("Date and amount required"); return; }
    try {
      setBusy(true);
      await updateAgeingP2P(caseId, p.id, {
        promised_at:     date,
        promised_amount: parseFloat(amount),
        method,
        notes,
      });
      toast.success("P2P updated");
      onSaved && (await onSaved());
    } catch (e) {
      toast.error("Failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="max-w-sm" data-testid={`cal-p2p-edit-dialog-${p.id}`}>
        <DialogHeader>
          <DialogTitle className="text-sm">Edit Promise to Pay</DialogTitle>
        </DialogHeader>
        <div className="space-y-2.5">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase">Promised date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-8 text-xs" data-testid="cal-p2p-edit-date" />
            </div>
            <div>
              <Label className="text-[10px] uppercase">Amount ({cur()})</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-8 text-xs" data-testid="cal-p2p-edit-amount" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase">Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                <SelectItem value="direct_debit">Direct debit</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Notes" className="text-xs" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="cal-p2p-edit-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ── Reschedule P2P (Missed → New P2P) — calendar local copy ───────────────
function CalP2PRescheduleDialog({ p, caseId, onClose, onSaved }) {
  const [date,   setDate]   = useState("");
  const [amount, setAmount] = useState(String(p.promised_amount ?? ""));
  const [method, setMethod] = useState(p.method || "bank_transfer");
  const [notes,  setNotes]  = useState("");
  const [busy,   setBusy]   = useState(false);

  const save = async () => {
    if (!date) { toast.error("New promise date required"); return; }
    try {
      setBusy(true);
      await rescheduleAgeingP2P(caseId, p.id, {
        promised_at:     date,
        promised_amount: parseFloat(amount) || undefined,
        method,
        notes,
        actor:           "Ops",
      });
      toast.success("P2P rescheduled — original marked broken, new promise logged");
      onSaved && (await onSaved());
    } catch (e) {
      toast.error("Failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="max-w-sm" data-testid={`cal-p2p-reschedule-dialog-${p.id}`}>
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-amber-600" />
            Reschedule missed P2P
          </DialogTitle>
          <p className="text-[11px] text-muted-foreground">
            The original promise ({cur()}{p.promised_amount?.toLocaleString()} on {p.promised_at}) will be marked <strong>broken</strong>, and a new promise created. Both events appear in the timeline.
          </p>
        </DialogHeader>
        <div className="space-y-2.5">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase">New promise date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-8 text-xs" data-testid="cal-p2p-reschedule-date" />
            </div>
            <div>
              <Label className="text-[10px] uppercase">New amount ({cur()})</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-8 text-xs" data-testid="cal-p2p-reschedule-amount" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase">Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                <SelectItem value="direct_debit">Direct debit</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Reason for reschedule (e.g. 'customer agreed new date after follow-up call')" className="text-xs" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !date} data-testid="cal-p2p-reschedule-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Reschedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ── Helpers ────────────────────────────────────────────────────────────
function isoDate(d) {
  // YYYY-MM-DD in local time (no UTC shift)
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function prettyDate(iso) {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString(locale(), {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
}

function shortName(name) {
  if (!name) return "";
  const n = name.replace(/\bLimited\b|\bLtd\b|\bPty\b/gi, "").trim();
  return n.length > 18 ? n.slice(0, 17) + "…" : n;
}
